/**
 * Display formatting. The API returns every numeric field as a string, so all
 * helpers accept `string | number | null | undefined` and degrade gracefully.
 */

function toNumber(value: string | number | null | undefined): number | null {
  if (value === null || value === undefined || value === '') return null;
  const n = typeof value === 'number' ? value : parseFloat(value);
  return Number.isFinite(n) ? n : null;
}

const inr = new Intl.NumberFormat('en-IN', {
  style: 'currency',
  currency: 'INR',
  maximumFractionDigits: 2,
  minimumFractionDigits: 2,
});

const inrCompact = new Intl.NumberFormat('en-IN', {
  style: 'currency',
  currency: 'INR',
  notation: 'compact',
  maximumFractionDigits: 2,
});

const decimal = new Intl.NumberFormat('en-IN', { maximumFractionDigits: 2 });
const fixed2 = new Intl.NumberFormat('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const integer = new Intl.NumberFormat('en-IN', { maximumFractionDigits: 0 });

/** ₹1,23,456.00 — the canonical money format for this app. */
export function formatINR(value: string | number | null | undefined, fallback = '—'): string {
  const n = toNumber(value);
  return n === null ? fallback : inr.format(n);
}

/** ₹1.23L — for tiles where the full figure would wrap. */
export function formatINRCompact(value: string | number | null | undefined, fallback = '—'): string {
  const n = toNumber(value);
  return n === null ? fallback : inrCompact.format(n);
}

export function formatNumber(value: string | number | null | undefined, fallback = '—'): string {
  const n = toNumber(value);
  return n === null ? fallback : decimal.format(n);
}

export function formatQty(value: string | number | null | undefined, fallback = '—'): string {
  const n = toNumber(value);
  return n === null ? fallback : integer.format(n);
}

/** 23,750.65 — always two decimals, for tickers where a changing digit count would make the text jump. */
export function formatPrice(value: string | number | null | undefined, fallback = '—'): string {
  const n = toNumber(value);
  return n === null ? fallback : fixed2.format(n);
}

/** +147.05 / -0.30 — signed, always two decimals (see `formatPrice`). */
export function formatSignedPrice(value: string | number | null | undefined, fallback = '—'): string {
  const n = toNumber(value);
  if (n === null) return fallback;
  return `${n > 0 ? '+' : ''}${fixed2.format(n)}`;
}

/** +1.82% / -0.37% — like `formatPct` but always two decimals, for fixed-width tickers. */
export function formatPctFixed(value: string | number | null | undefined, fallback = '—'): string {
  const n = toNumber(value);
  if (n === null) return fallback;
  return `${n > 0 ? '+' : ''}${fixed2.format(n)}%`;
}

/** +1.82% / -0.37% — sign is always explicit so direction reads at a glance. */
export function formatPct(value: string | number | null | undefined, fallback = '—'): string {
  const n = toNumber(value);
  if (n === null) return fallback;
  return `${n > 0 ? '+' : ''}${decimal.format(n)}%`;
}

export function formatSignedINR(value: string | number | null | undefined, fallback = '—'): string {
  const n = toNumber(value);
  if (n === null) return fallback;
  return `${n > 0 ? '+' : n < 0 ? '-' : ''}${inr.format(Math.abs(n))}`;
}

/** Tailwind class for a P&L value. Neutral at exactly zero. */
export function signClass(value: string | number | null | undefined): string {
  const n = toNumber(value);
  if (n === null || n === 0) return 'text-fg-muted';
  return n > 0 ? 'text-profit' : 'text-loss';
}

export function signOf(value: string | number | null | undefined): 1 | 0 | -1 {
  const n = toNumber(value);
  if (n === null || n === 0) return 0;
  return n > 0 ? 1 : -1;
}

export function toNum(value: string | number | null | undefined, fallback = 0): number {
  return toNumber(value) ?? fallback;
}

/** 29 Aug, 14:32 — compact enough for a table cell. */
export function formatDateTime(value: string | null | undefined, fallback = '—'): string {
  if (!value) return fallback;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return fallback;
  return d.toLocaleString('en-IN', {
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
}

export function formatTime(value: string | null | undefined, fallback = '—'): string {
  if (!value) return fallback;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return fallback;
  return d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false });
}

/** Short id for display: 8 chars + ellipsis. */
export function shortId(id: string | null | undefined): string {
  if (!id) return '—';
  return id.length <= 10 ? id : `${id.slice(0, 8)}…`;
}
